import React from 'react';
import '../App.css';
import { shorthands, makeStyles, Text, Divider } from '@fluentui/react-components';
import
{
    DataGrid,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridCell,
    DataGridRow,
    DataGridBody,
    TableColumnDefinition,
    createTableColumn,
    TableCellLayout,
    Button
} from '@fluentui/react-components';
import { rexDataGridStyles } from "../slStyles";


export interface EditItemDelegate<TItem, TEditArgs>
{
    (item: TItem, editArgs: TEditArgs): void;
}

export interface CanEditItemDelegate<TItem, TEditArgs>
{
    (item: TItem, editArgs: TEditArgs): boolean;
}

export interface GetRowIdDelegate<TItem>
{
    (item: TItem): string;
}

export interface ItemsListProps<TItem, TEditArgs>
{
    styles?: Record<string, string>;
    columns: TableColumnDefinition<TItem>[];
    items: TItem[];

    // currently, the actions for each row are defined in the column
    // property, so the host can reference their delegates directly.
    // BUT in the future we might do additional actions defined here, so
    // we want to get the hosts in the habit of providing these delegates.
    onEditItem: EditItemDelegate<TItem, TEditArgs>;
    canEditItem?: CanEditItemDelegate<TItem, TEditArgs>;
    editArgs: TEditArgs;

    getRowId: GetRowIdDelegate<TItem>;
    autofitContents?: boolean;
}

export interface ItemsListState
{
}

export const useStylesItemsList = makeStyles(
    {
        ...rexDataGridStyles.styles
    });

export class ItemsListWithoutStyles<TItem, TEditArgs> extends React.Component<ItemsListProps<TItem, TEditArgs>, ItemsListState>
{
    constructor(props: ItemsListProps<TItem, TEditArgs>)
    {
        super(props);
    }

    canEditThisItem(item: TItem): boolean
    {
        if (!this.props.canEditItem)
            return true;

        return this.props.canEditItem(item, this.props.editArgs);
    }

    onEditButtonClick(event: React.MouseEvent<HTMLButtonElement>, item: TItem)
    {
        this.props.onEditItem(item, this.props.editArgs);
        event.stopPropagation();
    }

    render()
    {
        const items = this.props.items;

        const gridBody = (<DataGridBody<TItem>>
                              {({ item, rowId }) => (
                                  <DataGridRow<TItem> key={rowId} selectionCell={{ checkboxIndicator: { "aria-label": "Select row" } }}>
                                      {({ renderCell }) => (
                        <DataGridCell>{renderCell(item)}</DataGridCell>)}
                                  </DataGridRow>)}
                          </DataGridBody>
        );

        return (
            <div>
                <DataGrid className={this.props.styles?.gridTable} items={items} columns={this.props.columns} sortable selectionMode="multiselect" subtleSelection
                          getRowId={this.props.getRowId}
                          noNativeElements={this.props.autofitContents ? false : true}
                          style={{ tableLayout: 'auto' }}>
                    <DataGridHeader className={this.props.styles?.headerRow}>
                        <DataGridRow selectionCell={{ checkboxIndicator: { "aria-label": "Select all rows" } }}>
                            {({ columnId, renderHeaderCell }) => (<DataGridHeaderCell key={columnId} className={this.props.styles?.headerCellProps}>{renderHeaderCell()}</DataGridHeaderCell>)}
                        </DataGridRow>
                    </DataGridHeader>
                    {gridBody}
                </DataGrid>
                {items.length == 0 && <div>There are no items to display</div>}
            </div>)
    }
}

export type ItemsListHOC = <T1, T2>(
    WrappedComponent: React.ComponentType<ItemsListProps<T1, T2>>
) => React.ComponentType<ItemsListProps<T1, T2>>;

export const ItemsListWithStyles: ItemsListHOC = (WrappedComponent) =>
{
    // Return a functional component that injects styles
    return function WithStylesWrapper(props)
    {
        const classes = useStylesItemsList();
        return <WrappedComponent {...props} styles={classes}/>;
    };
};